import type { Lesson } from '../schema.js';

// Colour Dash lessons: explain + quiz only. The facts match the game as built
// (packages/game-engine/src/colour-dash/play.ts): a 108-card deck of four colours, seven cards each, play a card that
// matches the colour in force, the number or the symbol (or a wild), draw one card when you cannot play, Skip /
// Reverse / Draw Two / Wild / Wild Draw Four, calling "last card", and scoring the cards left in the other hands to 500.
// The simple Wild Draw Four rule is used: it may be played at any time and there is no challenge. These rules are
// recalled, not checked against a rulebook, and a player should confirm them. The name of the best-known commercial
// game of this kind belongs to its owner and must never appear here (a test guards the feature's files).
export const COLOUR_DASH_LESSONS: Lesson[] = [
  {
    id: 'colour-dash-matching',
    slug: 'colour-dash-matching-cards',
    title: 'Colour Dash: matching cards',
    audienceLevel: 'beginner',
    concepts: ['colour-dash'],
    steps: [
      {
        kind: 'explain',
        title: 'Match the top card',
        body: 'In Colour Dash everyone gets seven cards, and one number card is turned up to start the pile. On your turn you play a card that matches the top of the pile in one of three ways: the same colour, the same number, or the same symbol. A wild card can be played on anything. If you cannot play, you draw one card from the stock. If that card fits you may play it, and if not your turn ends. The first player to play their last card wins the hand.',
        narration: 'Match the colour, the number or the symbol. Cannot play? Draw one card.',
      },
      {
        kind: 'quiz',
        title: 'Matching',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'The top card is a red 5. Which card can you play?',
            choices: ['A blue 5', 'A blue 7', 'A green skip'],
            correct: 0,
          },
          {
            prompt: 'The top card is a red 5. Which of these can you also play?',
            choices: ['A red 9', 'A yellow 2', 'A blue reverse'],
            correct: 0,
          },
          {
            prompt: 'You cannot play any card. What do you do?',
            choices: ['Draw one card', 'Draw three cards', 'Skip the rest of the game'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'colour-dash-actions',
    slug: 'colour-dash-action-and-wild-cards',
    title: 'Colour Dash: action cards',
    audienceLevel: 'beginner',
    concepts: ['colour-dash'],
    steps: [
      {
        kind: 'explain',
        title: 'Cards that change the game',
        body: 'A Skip makes the next player miss their turn. A Reverse turns the direction of play around, and with only two players it works like a Skip. A Draw Two makes the next player draw two cards and miss their turn. A Wild lets you name any colour for the next card. A Wild Draw Four does that too, and the next player also draws four cards and misses their turn. In our game you may play a Wild Draw Four at any time.',
        narration: 'Skip, Reverse, Draw Two and the two wilds change who plays and what colour comes next.',
      },
      {
        kind: 'quiz',
        title: 'Action cards',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'You play a Draw Two. What happens to the next player?',
            choices: ['They draw two cards and miss a turn', 'They draw two cards and then play', 'Nothing'],
            correct: 0,
          },
          {
            prompt: 'You play a Reverse in a game with only two players. It works like...',
            choices: ['A Skip', 'A Draw Two', 'A Wild'],
            correct: 0,
          },
          {
            prompt: 'You play a Wild. What do you do?',
            choices: ['Name the colour for the next card', 'Make the next player draw four', 'Take another turn'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'colour-dash-last-card',
    slug: 'colour-dash-last-card-and-scoring',
    title: 'Colour Dash: last card and scoring',
    audienceLevel: 'beginner',
    concepts: ['colour-dash'],
    steps: [
      {
        kind: 'explain',
        title: 'Call it, then count',
        body: 'When you are down to two cards, press "Last card" before you play one of them. If you play down to a single card without calling, you must draw two cards. The first player to play their last card wins the hand and scores the cards left in everyone else\'s hand: a number card is worth its number, a Skip, Reverse or Draw Two is worth 20, and a wild is worth 50. The points add up over several hands, and the first player to reach 500 wins the game.',
        narration: 'Say last card when you hold two. Going out scores the cards left in the other hands.',
      },
      {
        kind: 'quiz',
        title: 'Last card and scoring',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'You hold two cards. When must you call "Last card"?',
            choices: ['Before you play one of them', 'After the game ends', 'Never'],
            correct: 0,
          },
          {
            prompt: 'You go out. Another player still holds a Skip and a 7. How many points do you score from that hand?',
            choices: ['27', '7', '20'],
            correct: 0,
          },
          {
            prompt: 'How many points do you need to win the game?',
            choices: ['100', '500', '1000'],
            correct: 1,
          },
        ],
      },
    ],
  },
];
